import { useAppSelector } from "@/app/hooks";
import type { Property, Survey } from "@/features/survey/types";
import GroupTabStrip from "@/shared/components/GroupTabStrip";
import { usePropertyImage } from "@/shared/hooks/usePropertyImage";
import { hasAddressLine2AsCustom, renderUrlOrValue } from "@/shared/utils";
import {
  getActivePropertyFields,
  isFieldVisible,
} from "@/shared/utils/propertyFieldRules";
import {
  getPropertyValue,
  getShortlistedProperties,
  isShortlistEnabled,
} from "@/shared/utils/propertyUtils";
import { EmeraldIcon } from "@emerald-react/icon";
import type { KeyboardEvent, MouseEvent } from "react";
import { Navigate, useNavigate } from "react-router";

const MAX_SUMMARY_FIELDS = 4;

// Mirrors PropertyCard's title/address rules, driven by the property's own survey.
const getPropertyTitleAndAddress = (survey: Survey, property: Property) => {
  const hasFieldRules =
    !!survey.surveyPropertyFieldRule?.propertyFieldRuleList?.length;
  const isVisible = (fieldKey: string): boolean =>
    hasFieldRules ? isFieldVisible(survey, fieldKey) : true;

  const nameValue = property.name || property.propertyName;
  const hasRealName =
    !!nameValue && nameValue.trim() !== "" && nameValue !== "Untitled Property";
  const nameIsTitle = isVisible("name") && hasRealName;

  const stateZip = [
    isVisible("stateProvince") ? property.stateProvince : null,
    isVisible("postalCode") ? property.postalCode : null,
  ]
    .filter(Boolean)
    .join("  ");
  const cityStateZip = [
    isVisible("city") ? property.city : null,
    stateZip || null,
  ].filter(Boolean);

  const title = nameIsTitle
    ? nameValue!
    : property.address1 || "Unnamed Property";
  const address = nameIsTitle
    ? [
        isVisible("address1") ? property.address1?.replace(/,+\s*$/, "") : null,
        ...cityStateZip,
      ]
        .filter(Boolean)
        .join(", ")
    : cityStateZip.join(", ");

  return { title, address };
};

interface ShortlistRowProps {
  survey: Survey;
  property: Property;
}

const ShortlistRow = ({ survey, property }: ShortlistRowProps) => {
  const navigate = useNavigate();
  const { imageSrc, isLoading } = usePropertyImage(property);

  const { title, address } = getPropertyTitleAndAddress(survey, property);
  const summaryFields = getActivePropertyFields(survey, {
    excludeAddressFields: true,
    hasAddressLine2Custom: hasAddressLine2AsCustom(survey),
  }).slice(0, MAX_SUMMARY_FIELDS);

  const openProperty = () =>
    navigate(`/interactive/${survey.id}/property/${property.id}`);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    // Links rendered inside field values (hyperlink fields) keep their own behavior
    if ((event.target as HTMLElement).closest("a")) return;
    openProperty();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openProperty();
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`View details for ${title}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex flex-col gap-4 border border-gray-300 bg-white p-6 cursor-pointer transition-colors hover:bg-[#f3f5f4] focus-visible:bg-[#f3f5f4] focus-visible:outline-2 focus-visible:outline-primary md:flex-row md:items-center md:gap-6"
    >
      {isLoading ? (
        <div className="h-22 w-32 shrink-0 animate-pulse bg-gray-200" />
      ) : (
        <div
          role="img"
          aria-label={title}
          style={{ backgroundImage: `url(${imageSrc})` }}
          className="h-22 w-32 shrink-0 bg-secondary-lightest bg-cover bg-center bg-no-repeat"
        />
      )}

      <div className="min-w-0 flex-1">
        <h3 className="fancy prop-title truncate text-xl">{title}</h3>
        {address && (
          <p
            className="prop-card-address mt-1 line-clamp-2"
            style={{ color: "var(--color-default)" }}
          >
            {address}
          </p>
        )}
      </div>

      {summaryFields.length > 0 && (
        <dl className="flex flex-wrap gap-x-10 gap-y-3 md:flex-nowrap md:pr-4">
          {summaryFields.map((field) => {
            const value = getPropertyValue(
              field.fieldKey || "",
              field.fieldId || "",
              property,
            );
            return (
              <div key={field.fieldId} className="min-w-24 md:max-w-40">
                <dt className="text-xs text-data-light">{field.fieldLabel}</dt>
                <dd className="mt-1 text-base font-medium text-primary wrap-break-word">
                  {value === null || value === undefined || value === ""
                    ? "—"
                    : renderUrlOrValue(value, field.fieldKey)}
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      <EmeraldIcon
        icon="chevron_right"
        className="hidden shrink-0 text-primary md:block"
      />
    </div>
  );
};

/**
 * Interactive Survey Group Shortlist Page
 * Read-only list of every shortlisted property across the group's markets;
 * selecting a row opens that property's details page.
 */
const InteractiveSurveyGroupShortlist = () => {
  const { currentSurveyGroup, groupSurveys } = useAppSelector(
    (state) => state.interactiveSurvey,
  );

  if (!currentSurveyGroup) {
    return null;
  }

  const surveys = groupSurveys ?? [];

  if (!isShortlistEnabled(null, currentSurveyGroup)) {
    return (
      <Navigate
        to={`/interactive/group/${currentSurveyGroup.surveyGroupId}/cover`}
        replace
      />
    );
  }

  const marketsWithShortlist = surveys
    .map((survey) => ({ survey, properties: getShortlistedProperties(survey) }))
    .filter(({ properties }) => properties.length > 0);
  const totalCount = marketsWithShortlist.reduce(
    (total, { properties }) => total + properties.length,
    0,
  );
  const marketCount = marketsWithShortlist.length;

  return (
    // The app shell clips body overflow, so the page scrolls inside its own
    // container while the group tab strip stays pinned.
    <div className="flex h-screen flex-col bg-white [&>header]:shrink-0">
      <GroupTabStrip
        surveyGroup={currentSurveyGroup}
        surveys={surveys}
        activeTab="shortlist"
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
          <h1 className="fancy text-4xl text-primary">Shortlist</h1>
          <p className="mt-3 text-sm text-data-light">
            {totalCount === 0
              ? "No properties have been shortlisted yet."
              : `${totalCount} ${totalCount === 1 ? "property" : "properties"} shortlisted across ${marketCount} ${marketCount === 1 ? "market" : "markets"}. Select a property to view its details.`}
          </p>

          {marketsWithShortlist.map(({ survey, properties }) => (
            <section key={survey.id} className="mt-10" aria-label={survey.name}>
              <h2 className="fancy mb-4 text-2xl text-primary">
                {survey.name}
              </h2>
              <div className="flex flex-col gap-4">
                {properties.map((property) => (
                  <ShortlistRow
                    key={property.id}
                    survey={survey}
                    property={property}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractiveSurveyGroupShortlist;
